import EventEmitter from "events";
import { createChatCompletion, createStreamingChatCompletion } from "./openai";
import { PluginContext } from "../plugins/plugin-context";
import { pluginRunner } from "../plugins/plugin-runner";
import { Chat, Message, OpenAIMessage, Parameters, getOpenAIMessageFromMessage } from "./types";
import { EventEmitterAsyncIterator } from "../utils/event-emitter-async-iterator";
import { YChat } from "./y-chat";
import { OptionsManager } from "../options";
import { HARDCODED_VALUES } from "../../hardcoded-values";

export class ReplyRequest extends EventEmitter {
    private mutatedMessages: OpenAIMessage[];
    private mutatedParameters: Parameters;
    private lastChunkReceivedAt: number = 0;
    private timer: any;
    private done: boolean = false;
    private content = '';
    private cancelSSE: any;

    constructor(private chat: Chat,
                private yChat: YChat,
                private messages: Message[],
                private replyID: string,
                private requestedParameters: Parameters,
                private pluginOptions: OptionsManager) {
        super();
        this.mutatedMessages = [...messages];
        this.mutatedMessages = messages.map(m => getOpenAIMessageFromMessage(m));
        this.mutatedParameters = { ...requestedParameters };
    }

    pluginContext = (pluginID: string) => ({
        getOptions: () => {
            return this.pluginOptions.getAllOptions(pluginID, this.chat.id);
        },

        getCurrentChat: () => {
            return this.chat;
        },

        createChatCompletion: async (messages: OpenAIMessage[], _parameters: Parameters) => {
            return await createChatCompletion(messages, _parameters);
        },

        setChatTitle: async (title: string) => {
            this.yChat.title = title;
        },
    } as PluginContext);

    private scheduleTimeout() {
        this.lastChunkReceivedAt = Date.now();

        clearInterval(this.timer);

        this.timer = setInterval(() => {
            const sinceLastChunk = Date.now() - this.lastChunkReceivedAt;
            if (sinceLastChunk > 120000 && !this.done) {
                this.onError('BasedGPT nodded off. (Timed Out)');
            }
        }, 2000);
    }

    public async execute() {
        try {
            this.scheduleTimeout();

            await pluginRunner("preprocess-model-input", this.pluginContext, async plugin => {
                const output = await plugin.preprocessModelInput(this.mutatedMessages, this.mutatedParameters);
                this.mutatedMessages = output.messages;
                this.mutatedParameters = output.parameters;
                this.lastChunkReceivedAt = Date.now();
            });

            const { emitter, cancel } = await createStreamingChatCompletion(this.mutatedMessages, {
                ...this.mutatedParameters,
            });
            this.cancelSSE = cancel;

            const eventIterator = new EventEmitterAsyncIterator<string>(emitter, ["data", "done", "error"]);

            for await (const event of eventIterator) {
                const { eventName, value } = event;

                switch (eventName) {
                    case 'data':
                        await this.onData(value);
                        break;

                    case 'done':
                        await this.onDone();
                        break;

                    case 'error':
                        if (!this.content || !this.done) {
                            await this.onError(value);
                        }
                        break;
                }
            }
        } catch (e: any) {
            console.error(e);
            this.onError(e.message);
        }
    }

    public async onData(value: any) {
        if (this.done) {
            return;
        }

        this.lastChunkReceivedAt = Date.now();

        this.content = value;

        await pluginRunner("postprocess-model-output", this.pluginContext, async plugin => {
            const output = await plugin.postprocessModelOutput({
                role: 'assistant',
                content: this.content,
            }, this.mutatedMessages, this.mutatedParameters, false);

            this.content = output.content;
        });

        this.yChat.setPendingMessageContent(this.replyID, this.content);
    }

    public async onDone() {
        if (this.done) {
            return;
        }
        clearInterval(this.timer);
        this.lastChunkReceivedAt = Date.now();
        this.done = true;
        this.emit('done');

        this.yChat.onMessageDone(this.replyID);

        await pluginRunner("postprocess-model-output", this.pluginContext, async plugin => {
            const output = await plugin.postprocessModelOutput({
                role: 'assistant',
                content: this.content,
            }, this.mutatedMessages, this.mutatedParameters, true);

            this.content = output.content;
        });

        this.yChat.setMessageContent(this.replyID, this.content);
    }

    public async onError(error: string | { message: string, type: string, removeLastMessage?: boolean, action?: string, flaggedContent?: string, violatedCategories?: string }) {
        if (this.done) {
            return;
        }
        this.done = true;
        this.emit('done');
        clearInterval(this.timer);
        this.cancelSSE?.();

        let errorMessage: string;
        
        // Parse JSON string errors (from basic completion)
        let parsedError = error;
        if (typeof error === 'string') {
            try {
                parsedError = JSON.parse(error);
            } catch (e) {
                // Not JSON, keep as string
            }
        }
        
        // Check if this is a rate limit error
        if (typeof parsedError === 'object' && parsedError.type === 'RATE_LIMIT_EXCEEDED') {
            const hasDiscordLink = parsedError.message && parsedError.message.includes('Discord');
            const discordSuffix = hasDiscordLink ? '' : `\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`;
            errorMessage = `\n\n🚫 ${parsedError.message}${discordSuffix}`;
        } else if (typeof parsedError === 'object' && parsedError.type === 'CHARACTER_LIMIT') {
            // Special handling for character limit errors with more prominent styling
            const hasDiscordLink = parsedError.message && parsedError.message.includes('Discord');
            const discordSuffix = hasDiscordLink ? '' : `\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`;
            errorMessage = `\n\n📝 **Character Limit Exceeded**\n\n${parsedError.message}\n\n*Tip: Use the character counter below the input box to stay within the limit.*${discordSuffix}`;
        } else if (typeof parsedError === 'object' && parsedError.type === 'SESSION_EXPIRED') {
            // Special handling for session validation errors
            const hasDiscordLink = parsedError.message && parsedError.message.includes('Discord');
            const discordSuffix = hasDiscordLink ? '' : `\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`;
            errorMessage = `\n\n🔐 **Session Expired**\n\n${parsedError.message}\n\n*Your session has been refreshed automatically. Please try sending your message again.*${discordSuffix}`;
        } else if (typeof parsedError === 'object' && parsedError.type === 'TOKEN_EXPIRED') {
            // Special handling for completely expired tokens
            const hasDiscordLink = parsedError.message && parsedError.message.includes('Discord');
            const discordSuffix = hasDiscordLink ? '' : `\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`;
            errorMessage = `\n\n🔐 **Token Expired**\n\n${parsedError.message}\n\n*Please refresh the page to continue using BasedGPT.*${discordSuffix}`;
        } else if (typeof parsedError === 'object' && parsedError.type === 'TOOL_FAILED') {
            // Special handling for tool execution failures
            errorMessage = `\n\n${parsedError.message}`;
        } else if (typeof parsedError === 'object' && parsedError.type === 'API_ERROR') {
            // Special handling for external API errors
            errorMessage = `\n\n${parsedError.message}`;
        } else if (typeof parsedError === 'object' && parsedError.type === 'TIMEOUT') {
            // Special handling for timeout errors
            errorMessage = `\n\n${parsedError.message}`;
        } else if (typeof parsedError === 'object' && parsedError.type === 'CONTENT_MODERATION') {
            // Handle content moderation errors based on action
            if (parsedError.action === 'SHOW_FLAGGED_MESSAGE') {
                // Don't show an error message - the frontend will handle displaying the flagged message
                // Just signal that this is a flagged message case
                this.emit('flagged-message', {
                    content: parsedError.flaggedContent,
                    message: parsedError.message || 'Your message was flagged by our content policy',
                    categories: parsedError.violatedCategories
                });
                return; // Don't set error message content
            } else {
                // Legacy handling for other moderation actions
                const baseMessage = parsedError.message || 'Your message was blocked due to content policy violations.';
                const hasDiscordLink = baseMessage.includes('Discord');
                const discordSuffix = hasDiscordLink ? '' : `\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`;
                errorMessage = `\n\n⚠️ **Message Blocked**\n\n${baseMessage}\n\n*The message has been removed from the conversation.*${discordSuffix}`;
                
                // Signal to remove the last user message
                if (parsedError.removeLastMessage) {
                    try {
                        // Remove the last user message from the chat
                        this.yChat.removeLastUserMessage();
                    } catch (removeError) {
                        console.error('[MODERATION] Failed to remove message:', removeError);
                        // Continue anyway - we still want to show the error message
                    }
                }
            }
        } else {
            const errorText = typeof parsedError === 'string' ? parsedError : (typeof parsedError === 'object' ? parsedError.message : String(error));
            // Check if Discord link already exists to avoid duplication
            const hasDiscordLink = typeof errorText === 'string' && (errorText.includes('Discord') || errorText.includes('discord.gg'));
            const discordSuffix = hasDiscordLink ? '' : `\n\n💬 Need help? Report the error in our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`;
            errorMessage = `\n\nI'm sorry, BasedGPT is on break (${errorText || 'No response from the API'})${discordSuffix}`;
        }

        this.content += errorMessage;
        this.content = this.content.trim();

        this.yChat.setMessageContent(this.replyID, this.content);
        this.yChat.onMessageDone(this.replyID);
    }

    public onCancel() {
        clearInterval(this.timer);
        this.done = true;
        this.yChat.onMessageDone(this.replyID);
        this.cancelSSE?.();
        this.emit('done');
    }

    // private setMessageContent(content: string) {
    //     const text = this.yChat.content.get(this.replyID);
    //     if (text && text.toString() !== content) {
    //         text?.delete(0, text.length);
    //         text?.insert(0, content);
    //     }
    // }
}
